import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LoadingScreen } from '../components/ui/Spinner';
import { handleImageError } from '../utils/imageUtils';
import { ArrowLeftIcon, PencilIcon, CheckIcon, XMarkIcon, TrashIcon, PlusIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import ChatPanel from '../components/ChatPanel';
import { formatCurrency, formatDateTime, formatDuration } from '../utils/formatting';
import PlayerTypeIcon from '../components/PlayerTypeIcon';

// Tag choices for notable hands - must match backend BigHand.TAG_CHOICES
const HAND_TAG_CHOICES = [
    { value: 'discipline_fold', label: 'Discipline Fold', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
    { value: 'curiosity_call', label: 'Curiosity Call', color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' },
    { value: 'cooler', label: 'Cooler', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    { value: 'big_pot_won', label: 'Big Pot Won', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
    { value: 'bad_beat', label: 'Bad Beat', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
    { value: 'hero_call', label: 'Hero Call', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
    { value: 'bluff_success', label: 'Successful Bluff', color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' },
    { value: 'bluff_fail', label: 'Failed Bluff', color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' },
    { value: 'review_needed', label: 'Review Needed', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
];

const OUTCOME_CHOICES = [
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
];

// Tilt trigger states tracked during live session
const TILT_TRIGGERS = [
    { value: 'hunger', label: 'Hunger', emoji: '🍔', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
    { value: 'fatigue', label: 'Fatigue', emoji: '😴', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
    { value: 'frustration', label: 'Frustration', emoji: '😤', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    { value: 'ego', label: 'Ego', emoji: '👑', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
    { value: 'big_win', label: 'Big Win', emoji: '🚀', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
    { value: 'big_loss', label: 'Big Loss', emoji: '💔', color: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300' },
    { value: 'entitlement', label: 'Entitlement', emoji: '🤑', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
    { value: 'apathy', label: 'Apathy', emoji: '😐', color: 'bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300' },
];

function NotableHandCard({ hand, onUpdate }) {
    const [isEditing, setIsEditing] = useState(false);
    const [selectedTags, setSelectedTags] = useState(hand.tags || []);
    const [outcome, setOutcome] = useState(hand.outcome || 'won');
    const [amount, setAmount] = useState(hand.amount || 0);
    const [description, setDescription] = useState(hand.hand_description || '');
    const [saving, setSaving] = useState(false);

    // Sync local state when hand changes from parent
    useEffect(() => {
        setSelectedTags(hand.tags || []);
        setOutcome(hand.outcome || 'won');
        setAmount(hand.amount || 0);
        setDescription(hand.hand_description || '');
    }, [hand.tags, hand.outcome, hand.amount, hand.hand_description]);

    const handleTagToggle = (tagValue) => {
        setSelectedTags(prev =>
            prev.includes(tagValue)
                ? prev.filter(t => t !== tagValue)
                : [...prev, tagValue]
        );
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.patch(`/api/big-hands/${hand.id}/`, {
                tags: selectedTags,
                outcome: outcome,
                amount: parseFloat(amount) || 0,
                hand_description: description,
            });
            await onUpdate();
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to update hand:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setSelectedTags(hand.tags || []);
        setOutcome(hand.outcome || 'won');
        setAmount(hand.amount || 0);
        setDescription(hand.hand_description || '');
        setIsEditing(false);
    };

    const getTagColor = (tagValue) => {
        const tag = HAND_TAG_CHOICES.find(t => t.value === tagValue);
        return tag?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    };

    const getTagLabel = (tagValue) => {
        const tag = HAND_TAG_CHOICES.find(t => t.value === tagValue);
        return tag?.label || tagValue;
    };

    return (
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                    <p className="text-sm text-gray-900 dark:text-white font-medium whitespace-pre-wrap">
                        {hand.hand_description}
                    </p>
                    {hand.amount > 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {hand.outcome_display}: {formatCurrency(hand.amount)}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                    <span className="text-xs text-gray-400">
                        {formatDateTime(hand.timestamp)}
                    </span>
                    {!isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                            title="Edit hand"
                        >
                            <PencilIcon className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Current Tags Display */}
            {!isEditing && hand.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {hand.tags.map((tag, idx) => (
                        <span
                            key={idx}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTagColor(tag)}`}
                        >
                            {getTagLabel(tag)}
                        </span>
                    ))}
                </div>
            )}

            {/* Hand Editor */}
            {isEditing && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-3">
                    {/* Description */}
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                                     bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                     focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Outcome and Amount */}
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Outcome</label>
                            <select
                                value={outcome}
                                onChange={(e) => setOutcome(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {OUTCOME_CHOICES.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Amount ($)</label>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                         focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Tags */}
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Tags:</p>
                        <div className="flex flex-wrap gap-2">
                            {HAND_TAG_CHOICES.map((tag) => (
                                <button
                                    key={tag.value}
                                    type="button"
                                    onClick={() => handleTagToggle(tag.value)}
                                    className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                                        selectedTags.includes(tag.value)
                                            ? tag.color + ' ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    {tag.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                        <button
                            onClick={handleCancel}
                            className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function SessionDetailsPage() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [sessionPlayers, setSessionPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditingNotes, setIsEditingNotes] = useState(false);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [chatPanelOpen, setChatPanelOpen] = useState(false);

    // Checkpoint editing state
    const [editingCheckpointId, setEditingCheckpointId] = useState(null);
    const [deleteCheckpointId, setDeleteCheckpointId] = useState(null);
    const [showCheckpointModal, setShowCheckpointModal] = useState(false);
    const [checkpointForm, setCheckpointForm] = useState({
        stack_size: '',
        pnl: '',
        notes: '',
    });

    // Add Notable Hand state
    const [showAddHandModal, setShowAddHandModal] = useState(false);
    const [handTags, setHandTags] = useState([]);
    const [newTagName, setNewTagName] = useState('');
    const [addHandForm, setAddHandForm] = useState({
        description: '',
        amount: '',
        outcome: 'won',
        players_involved_ids: [],
        tags: [],
    });

    // Discipline fold state (legacy - hidden)
    const [showBigFoldModal, setShowBigFoldModal] = useState(false);
    const [bigFoldForm, setBigFoldForm] = useState({
        hand_log: '',
        call_amount: '',
        description: '',
    });

    // Mistake state
    const [showMistakeModal, setShowMistakeModal] = useState(false);
    const [mistakeForm, setMistakeForm] = useState({
        mistake_type_id: '',
        leak_ids: [],
        description: '',
        cost_estimate: '',
    });
    const [mistakeTypes, setMistakeTypes] = useState([]);
    const [leaks, setLeaks] = useState([]);

    useEffect(() => {
        const fetchSession = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axios.get(`/api/sessions/${sessionId}/`);
                setSession(response.data);
                setNotes(response.data.notes || '');
            } catch (err) {
                console.error('Failed to fetch session:', err);
                setError('Failed to load session details');
            } finally {
                setLoading(false);
            }
        };

        const fetchSessionPlayers = async () => {
            try {
                const response = await axios.get('/api/session-players/', {
                    params: { session: sessionId }
                });
                setSessionPlayers(response.data?.results || response.data || []);
            } catch (err) {
                console.error('Failed to fetch session players:', err);
            }
        };

        const fetchHandTags = async () => {
            try {
                const response = await axios.get('/api/hand-tags/');
                setHandTags(response.data?.results || response.data || []);
            } catch (err) {
                console.error('Failed to fetch hand tags:', err);
            }
        };

        if (sessionId) {
            fetchSession();
            fetchSessionPlayers();
            fetchHandTags();
        }
    }, [sessionId]);

    useEffect(() => {
        const fetchMistakeData = async () => {
            try {
                const [typesResponse, leaksResponse] = await Promise.all([
                    axios.get('/api/mistake-types/'),
                    axios.get('/api/leaks/')
                ]);
                setMistakeTypes(typesResponse.data?.results || typesResponse.data || []);
                setLeaks(leaksResponse.data?.results || leaksResponse.data || []);
            } catch (err) {
                console.error('Failed to fetch mistake data:', err);
            }
        };

        fetchMistakeData();
    }, []);

    const handleSaveNotes = async () => {
        setSaving(true);
        try {
            const response = await axios.patch(`/api/sessions/${sessionId}/`, {
                notes: notes
            });
            setSession(response.data);
            setIsEditingNotes(false);
        } catch (err) {
            console.error('Failed to save notes:', err);
            setError('Failed to save notes');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelEdit = () => {
        setNotes(session?.notes || '');
        setIsEditingNotes(false);
    };

    const createHandTag = async (name) => {
        try {
            const response = await axios.post('/api/hand-tags/get_or_create/', { name, color: 'blue' });
            if (response.data.created) {
                setHandTags(prev => [...prev, response.data]);
            }
            return response.data;
        } catch (err) {
            console.error('Failed to create hand tag:', err);
            return null;
        }
    };

    const handleAddHand = async (e) => {
        e.preventDefault();
        try {
            const tags = [...addHandForm.tags];
            if (tags.length === 0) {
                tags.push('review_needed');
            }

            await axios.post('/api/big-hands/', {
                session: sessionId,
                hand_description: addHandForm.description,
                outcome: addHandForm.outcome,
                amount: addHandForm.amount ? parseFloat(addHandForm.amount) : 0,
                players_involved_ids: addHandForm.players_involved_ids,
                tags: tags,
                notes: '',
            });

            // Refresh session data
            const response = await axios.get(`/api/sessions/${sessionId}/`);
            setSession(response.data);

            setShowAddHandModal(false);
            setAddHandForm({ description: '', amount: '', outcome: 'won', players_involved_ids: [], tags: [] });
            setNewTagName('');
        } catch (err) {
            console.error('Failed to add hand:', err);
        }
    };

    const handleEditCheckpoint = (checkpoint) => {
        setEditingCheckpointId(checkpoint.id);
        setCheckpointForm({
            stack_size: checkpoint.stack_size || '',
            pnl: checkpoint.pnl || '',
            notes: checkpoint.notes || '',
        });
        setShowCheckpointModal(true);
    };

    const handleSaveCheckpoint = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await axios.patch(`/api/session-checkpoints/${editingCheckpointId}/`, {
                stack_size: parseFloat(checkpointForm.stack_size),
                pnl: parseFloat(checkpointForm.pnl),
                notes: checkpointForm.notes,
            });

            // Refetch session to get updated checkpoints
            const response = await axios.get(`/api/sessions/${sessionId}/`);
            setSession(response.data);

            setShowCheckpointModal(false);
            setCheckpointForm({ stack_size: '', pnl: '', notes: '' });
            setEditingCheckpointId(null);
        } catch (err) {
            console.error('Failed to save checkpoint:', err);
            setError('Failed to save checkpoint');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCheckpoint = async () => {
        try {
            await axios.delete(`/api/session-checkpoints/${deleteCheckpointId}/`);

            // Refetch session to get updated checkpoints
            const response = await axios.get(`/api/sessions/${sessionId}/`);
            setSession(response.data);

            setDeleteCheckpointId(null);
        } catch (err) {
            console.error('Failed to delete checkpoint:', err);
            setError('Failed to delete checkpoint');
        }
    };

    const handleCancelCheckpoint = () => {
        setShowCheckpointModal(false);
        setCheckpointForm({ stack_size: '', pnl: '', notes: '' });
        setEditingCheckpointId(null);
    };

    const handleAddBigFold = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/api/big-hands/', {
                session: sessionId,
                hand_description: bigFoldForm.hand_log,
                hand_type: 'fold',
                amount: bigFoldForm.call_amount ? parseFloat(bigFoldForm.call_amount) : null,
                notes: bigFoldForm.description,
                decision_quality: 'correct',
            });

            // Refresh session data
            const response = await axios.get(`/api/sessions/${sessionId}/`);
            setSession(response.data);
            setShowBigFoldModal(false);
            setBigFoldForm({ hand_log: '', call_amount: '', description: '' });
        } catch (err) {
            console.error('Failed to add discipline fold:', err);
        }
    };

    const handleAddMistake = async (e) => {
        e.preventDefault();
        try {
            // Get mistake type name for notes
            const mistakeType = mistakeTypes.find(mt => mt.id === parseInt(mistakeForm.mistake_type_id));
            const mistakeTypeName = mistakeType ? mistakeType.name : '';
            const notes = mistakeTypeName ? `Mistake Type: ${mistakeTypeName}` : '';

            await axios.post('/api/big-hands/', {
                session: sessionId,
                hand_description: mistakeForm.description,
                hand_type: 'spot',
                amount: mistakeForm.cost_estimate ? parseFloat(mistakeForm.cost_estimate) : null,
                notes: notes,
                decision_quality: 'mistake',
                severity: 'moderate',
                leaks_ids: mistakeForm.leak_ids.map(id => parseInt(id)),
            });

            // Refresh session data
            const response = await axios.get(`/api/sessions/${sessionId}/`);
            setSession(response.data);
            setShowMistakeModal(false);
            setMistakeForm({ mistake_type_id: '', leak_ids: [], description: '', cost_estimate: '' });
        } catch (err) {
            console.error('Failed to add mistake:', err);
        }
    };

    if (loading) {
        return <LoadingScreen />;
    }

    if (error || !session) {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                    <p className="text-red-600 dark:text-red-400">{error || 'Session not found'}</p>
                </div>
                <button
                    onClick={() => navigate('/sessions')}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to Sessions
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={() => navigate('/sessions')}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                    <ArrowLeftIcon className="h-4 w-4 mr-2" />
                    Back to Sessions
                </button>
                <div className="flex items-center gap-3">
                    {session.status === 'completed' && (
                        <>
                            <button
                                onClick={() => setChatPanelOpen(true)}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                            >
                                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                                Review Chat
                            </button>
                            <button
                                onClick={() => navigate(`/sessions/${sessionId}/review`)}
                                className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                                    session.reviewed
                                        ? 'text-green-700 bg-green-100 border border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700'
                                        : 'text-white bg-primary-600 hover:bg-primary-700'
                                }`}
                            >
                                {session.reviewed ? 'Reviewed' : 'Review Session'}
                                {!session.reviewed && session.unreviewed_hands_count > 0 && (
                                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-white/20 rounded">
                                        {session.unreviewed_hands_count}
                                    </span>
                                )}
                            </button>
                        </>
                    )}
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Session Details</h1>
                </div>
            </div>

            {/* Session Information */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Basic Info */}
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Session Information</h2>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Game Type:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{session.game_type}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Location:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{session.location?.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Stake:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{session.stake?.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Start Time:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{formatDateTime(session.start_time)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">End Time:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{formatDateTime(session.end_time)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{formatDuration(session.duration)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Financial Info */}
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Financial Information</h2>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Buy-in:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(session.buy_in)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Cash-out:</span>
                                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(session.cash_out)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Profit/Loss:</span>
                                <span className={`font-medium ${session.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {formatCurrency(session.profit)}
                                </span>
                            </div>
                            {session.rakeback && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Rakeback:</span>
                                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(session.rakeback)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Session Performance Grades */}
                {(session.technical_grade || session.mental_grade) && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Session Performance</h3>
                        <div className="grid grid-cols-2 gap-4">
                            {session.technical_grade && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Technical Grade:</span>
                                    <span className={`text-2xl font-bold px-4 py-2 rounded-lg ${
                                        session.technical_grade === 'A' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                        session.technical_grade === 'B' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                                        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                    }`}>
                                        {session.technical_grade}
                                    </span>
                                </div>
                            )}
                            {session.mental_grade && (
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Mental Grade:</span>
                                    <span className={`text-2xl font-bold px-4 py-2 rounded-lg ${
                                        session.mental_grade === 'A' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                                        session.mental_grade === 'B' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' :
                                        'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                                    }`}>
                                        {session.mental_grade}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Players */}
            {/* Player History */}
            {sessionPlayers.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Player History ({sessionPlayers.length} encounters)
                    </h2>
                    <div className="space-y-3">
                        {sessionPlayers.map((sessionPlayer) => (
                            <div
                                key={sessionPlayer.id}
                                className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                                onClick={() => navigate(`/players/${sessionPlayer.player.id}`)}
                            >
                                <div className="flex items-center gap-3">
                                    {sessionPlayer.player.profile_picture_url ? (
                                        <img
                                            src={sessionPlayer.player.profile_picture_url}
                                            alt={sessionPlayer.player.name}
                                            className="w-10 h-10 rounded-full object-cover"
                                            onError={(e) => handleImageError(e, sessionPlayer.player.name?.[0] || '?')}
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-medium">
                                            {sessionPlayer.player.name.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <PlayerTypeIcon playerType={sessionPlayer.player.player_category} size="w-3 h-3" />
                                            <span className="text-gray-900 dark:text-white font-medium">
                                                {sessionPlayer.player.name}
                                            </span>
                                            {sessionPlayer.seat_position && (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                                    Seat {sessionPlayer.seat_position}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {sessionPlayer.left_at ? (
                                                <span>
                                                    {formatDateTime(sessionPlayer.joined_at)} → {formatDateTime(sessionPlayer.left_at)}
                                                    <span className="ml-2 text-gray-400">(Left table)</span>
                                                </span>
                                            ) : (
                                                <span>
                                                    Joined {formatDateTime(sessionPlayer.joined_at)}
                                                    <span className="ml-2 text-green-600 dark:text-green-400">(Still playing)</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Checkpoints */}
            {session.checkpoints && session.checkpoints.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Session Checkpoints ({session.checkpoints.length})
                    </h2>
                    <div className="space-y-4">
                        {session.checkpoints.map((checkpoint, index) => (
                            <div key={checkpoint.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                            Checkpoint {index + 1}
                                        </span>
                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                            {new Date(checkpoint.timestamp).toLocaleTimeString()}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleEditCheckpoint(checkpoint)}
                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                            title="Edit checkpoint"
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => setDeleteCheckpointId(checkpoint.id)}
                                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                            title="Delete checkpoint"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 mb-3">
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Stack Size</p>
                                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                            {formatCurrency(checkpoint.stack_size)}
                                        </p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">P&L</p>
                                        <p className={`text-lg font-semibold ${
                                            checkpoint.pnl > 0
                                                ? 'text-green-600 dark:text-green-400'
                                                : checkpoint.pnl < 0
                                                    ? 'text-red-600 dark:text-red-400'
                                                    : 'text-gray-600 dark:text-gray-400'
                                        }`}>
                                            {formatCurrency(checkpoint.pnl)}
                                        </p>
                                    </div>
                                </div>
                                {checkpoint.notes && (
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                        <p className="text-sm text-gray-700 dark:text-gray-300">{checkpoint.notes}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Discipline Folds - filtered from big_hands */}
            {(() => {
                const disciplineFolds = session.big_hands?.filter(h =>
                    h.hand_type === 'fold' && h.decision_quality === 'correct'
                ) || [];
                return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Discipline Folds ({disciplineFolds.length})
                    </h2>
                    <button
                        onClick={() => setShowBigFoldModal(true)}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
                    >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Discipline Fold
                    </button>
                </div>
                <div className="space-y-3">
                    {disciplineFolds.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                            No discipline folds logged for this session
                        </p>
                    ) : (
                        disciplineFolds.map(fold => (
                            <div key={fold.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {formatDateTime(fold.timestamp)}
                                    </span>
                                    {fold.amount && (
                                        <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                            Saved: {formatCurrency(fold.amount)}
                                        </span>
                                    )}
                                </div>
                                <div className="mb-2">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hand Details:</p>
                                    <p className="text-sm text-gray-900 dark:text-white font-medium whitespace-pre-wrap">
                                        {fold.hand_description}
                                    </p>
                                </div>
                                {fold.notes && (
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reasoning:</p>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                            {fold.notes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
                );
            })()}

            {/* Mistakes */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Session Mistakes ({session.mistakes?.length || 0})
                    </h2>
                    <button
                        onClick={() => setShowMistakeModal(true)}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Mistake
                    </button>
                </div>
                <div className="space-y-3">
                    {!session.mistakes || session.mistakes.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                            No mistakes logged for this session
                        </p>
                    ) : (
                        session.mistakes.map((mistake) => (
                            <div key={mistake.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="w-3 h-3 rounded-full"
                                            style={{ backgroundColor: mistake.mistake_type_color || '#f97316' }}
                                        ></div>
                                        <span className="font-medium text-gray-900 dark:text-white">
                                            {mistake.mistake_type_name}
                                        </span>
                                    </div>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {formatDateTime(mistake.timestamp)}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                                    {mistake.description}
                                </p>
                                {mistake.cost_estimate && (
                                    <p className="text-sm text-red-600 dark:text-red-400">
                                        Estimated Cost: {formatCurrency(mistake.cost_estimate)}
                                    </p>
                                )}
                                {mistake.leaks && mistake.leaks.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {mistake.leaks.map(leak => (
                                            <span key={leak.id} className="text-xs px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded">
                                                {leak.name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Curiosity Calls - filtered from big_hands */}
            {(() => {
                const curiosityCalls = session.big_hands?.filter(h =>
                    h.tags?.includes('curiosity_call') ||
                    (h.hand_type === 'call' && h.decision_quality === 'mistake')
                ) || [];
                return (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Curiosity Calls ({curiosityCalls.length})
                    </h2>
                </div>
                <div className="space-y-3">
                    {curiosityCalls.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                            No curiosity calls logged for this session
                        </p>
                    ) : (
                        curiosityCalls.map((call) => (
                            <div key={call.id} className="border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-cyan-600 dark:text-cyan-400 font-medium">
                                            {call.severity === 'severe' ? '🔥 Severe Call' : '😬 Bad Call'}
                                        </span>
                                    </div>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {formatDateTime(call.timestamp)}
                                    </span>
                                </div>
                                {call.amount && (
                                    <p className="text-sm text-cyan-700 dark:text-cyan-300 mb-2">
                                        Call Amount: {formatCurrency(call.amount)}
                                    </p>
                                )}
                                <div className="mb-2">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Hand Log:</p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                        {call.hand_description}
                                    </p>
                                </div>
                                {call.notes && (
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Description:</p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">
                                        {call.notes}
                                    </p>
                                </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
                );
            })()}

            {/* State Logs (Tilt Triggers) */}
            {session.state_logs?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Tilt Triggers Logged ({session.state_logs.length})
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        {TILT_TRIGGERS.map(trigger => {
                            const count = session.state_logs.filter(s => s.state_type === trigger.value).length;
                            if (count === 0) return null;
                            return (
                                <div key={trigger.value} className={`p-3 rounded-lg ${trigger.color}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xl">{trigger.emoji}</span>
                                        <div>
                                            <div className="font-medium">{trigger.label}</div>
                                            <div className="text-xs opacity-75">{count} time{count > 1 ? 's' : ''}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Timeline:</p>
                        <div className="space-y-2">
                            {session.state_logs.map((log, idx) => {
                                const trigger = TILT_TRIGGERS.find(t => t.value === log.state_type);
                                return (
                                    <div key={idx} className={`p-2 rounded ${trigger?.color || 'bg-gray-100 text-gray-700'}`}>
                                        <div className="flex items-center gap-2 text-sm">
                                            <span>{trigger?.emoji}</span>
                                            <span className="font-medium">{trigger?.label}</span>
                                            <span className="opacity-75">- {formatDateTime(log.timestamp)}</span>
                                        </div>
                                        {log.notes && (
                                            <p className="text-xs mt-1 opacity-90 italic">"{log.notes}"</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Notable Hands */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Notable Hands ({session.big_hands?.length || 0})
                    </h2>
                    <button
                        onClick={() => setShowAddHandModal(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        <PlusIcon className="w-4 h-4" />
                        Add Hand
                    </button>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Add tags to categorize hands for later review.
                </p>
                <div className="space-y-3">
                    {!session.big_hands || session.big_hands.length === 0 ? (
                        <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                            No notable hands logged for this session
                        </p>
                    ) : (
                        session.big_hands.map((hand) => (
                            <NotableHandCard
                                key={hand.id}
                                hand={hand}
                                onUpdate={async () => {
                                    const response = await axios.get(`/api/sessions/${sessionId}/`);
                                    setSession(response.data);
                                }}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Notes */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notes</h2>
                    {!isEditingNotes ? (
                        <button
                            onClick={() => setIsEditingNotes(true)}
                            className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                        >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Edit
                        </button>
                    ) : (
                        <div className="flex space-x-2">
                            <button
                                onClick={handleSaveNotes}
                                disabled={saving}
                                className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                <CheckIcon className="h-4 w-4 mr-1" />
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={handleCancelEdit}
                                className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                            >
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
                
                {isEditingNotes ? (
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-primary-400 dark:focus:border-primary-400"
                        placeholder="Add your notes about this session..."
                    />
                ) : (
                    <div className="min-h-[8rem] p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        {session.notes ? (
                            <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{session.notes}</p>
                        ) : (
                            <p className="text-gray-500 dark:text-gray-400 italic">No notes added yet.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Add Notable Hand Modal */}
            {showAddHandModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Notable Hand</h2>
                            <button onClick={() => setShowAddHandModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Log a hand you want to review later.
                        </p>
                        <form onSubmit={handleAddHand} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    What happened?
                                </label>
                                <textarea
                                    value={addHandForm.description}
                                    onChange={(e) => setAddHandForm({...addHandForm, description: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="e.g., AK vs QQ all-in pre, set over set on flop..."
                                    rows="3"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Result
                                    </label>
                                    <select
                                        value={addHandForm.outcome}
                                        onChange={(e) => setAddHandForm({...addHandForm, outcome: e.target.value})}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    >
                                        <option value="won">Won</option>
                                        <option value="lost">Lost</option>
                                        <option value="chopped">Chopped</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Amount (optional)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={addHandForm.amount}
                                        onChange={(e) => setAddHandForm({...addHandForm, amount: e.target.value})}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                        placeholder="$"
                                    />
                                </div>
                            </div>
                            {/* Villains involved */}
                            {sessionPlayers.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Villains involved (optional)
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {sessionPlayers.map(sp => (
                                            <button
                                                key={sp.player.id}
                                                type="button"
                                                onClick={() => {
                                                    const ids = addHandForm.players_involved_ids;
                                                    if (ids.includes(sp.player.id)) {
                                                        setAddHandForm({...addHandForm, players_involved_ids: ids.filter(id => id !== sp.player.id)});
                                                    } else {
                                                        setAddHandForm({...addHandForm, players_involved_ids: [...ids, sp.player.id]});
                                                    }
                                                }}
                                                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                                    addHandForm.players_involved_ids.includes(sp.player.id)
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                }`}
                                            >
                                                {sp.player.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Tags (optional)
                                </label>
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {handTags.map(tag => (
                                        <button
                                            key={tag.id}
                                            type="button"
                                            onClick={() => {
                                                const tags = addHandForm.tags;
                                                if (tags.includes(tag.name)) {
                                                    setAddHandForm({...addHandForm, tags: tags.filter(t => t !== tag.name)});
                                                } else {
                                                    setAddHandForm({...addHandForm, tags: [...tags, tag.name]});
                                                }
                                            }}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                                addHandForm.tags.includes(tag.name)
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {tag.name}
                                        </button>
                                    ))}
                                </div>
                                {/* Create new tag inline */}
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newTagName}
                                        onChange={(e) => setNewTagName(e.target.value)}
                                        placeholder="New tag name..."
                                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (newTagName.trim()) {
                                                const tag = await createHandTag(newTagName.trim());
                                                if (tag) {
                                                    setAddHandForm({...addHandForm, tags: [...addHandForm.tags, tag.name]});
                                                    setNewTagName('');
                                                }
                                            }
                                        }}
                                        disabled={!newTagName.trim()}
                                        className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                                    >
                                        Add
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add Hand
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Checkpoint Modal */}
            {showCheckpointModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Edit Checkpoint
                            </h2>
                            <button onClick={handleCancelCheckpoint}>
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveCheckpoint} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Stack Size
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={checkpointForm.stack_size}
                                    onChange={(e) => setCheckpointForm({...checkpointForm, stack_size: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                    placeholder="Current chip stack"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Profit/Loss
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={checkpointForm.pnl}
                                    onChange={(e) => setCheckpointForm({...checkpointForm, pnl: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    required
                                    placeholder="Profit/Loss since session start"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Notes (optional)
                                </label>
                                <textarea
                                    value={checkpointForm.notes}
                                    onChange={(e) => setCheckpointForm({...checkpointForm, notes: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="3"
                                    placeholder="Any observations or thoughts at this checkpoint..."
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={handleCancelCheckpoint}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Checkpoint Confirmation Modal */}
            {deleteCheckpointId && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Checkpoint</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Are you sure you want to delete this checkpoint? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteCheckpointId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteCheckpoint}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Discipline Fold Modal */}
            {showBigFoldModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Discipline Fold</h2>
                            <button onClick={() => setShowBigFoldModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                            </button>
                        </div>
                        <form onSubmit={handleAddBigFold} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Hand Log
                                </label>
                                <textarea
                                    value={bigFoldForm.hand_log}
                                    onChange={(e) => setBigFoldForm({...bigFoldForm, hand_log: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="4"
                                    placeholder="Hero: AK&#10;Board: K♥ 9♠ 3♦ 8♣ 2♥&#10;Pot: $450&#10;Villain shoved $200..."
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Include your hand, board texture, pot size, and players involved
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Call Amount <span className="text-gray-400">(optional)</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={bigFoldForm.call_amount}
                                    onChange={(e) => setBigFoldForm({...bigFoldForm, call_amount: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="200.00"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Amount you would have needed to call (helps track money saved)
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description / Reasoning
                                </label>
                                <textarea
                                    value={bigFoldForm.description}
                                    onChange={(e) => setBigFoldForm({...bigFoldForm, description: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="4"
                                    placeholder="Reasoning: Villain had been tight all night, this was his first 3-bet. Even though I had top pair top kicker, his range here is so strong...&#10;&#10;Confidence: High&#10;Outcome: He showed pocket kings"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Include your reasoning, confidence level, and outcome if known
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowBigFoldModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                                >
                                    Add Discipline Fold
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Mistake Modal */}
            {showMistakeModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Mistake</h2>
                            <button onClick={() => setShowMistakeModal(false)}>
                                <XMarkIcon className="h-6 w-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" />
                            </button>
                        </div>
                        <form onSubmit={handleAddMistake} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Mistake Type
                                </label>
                                <select
                                    value={mistakeForm.mistake_type_id}
                                    onChange={(e) => setMistakeForm({...mistakeForm, mistake_type_id: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                >
                                    <option value="">Select a mistake type</option>
                                    {mistakeTypes.map(type => (
                                        <option key={type.id} value={type.id}>
                                            {type.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Related Leaks <span className="text-gray-400">(optional)</span>
                                </label>
                                <select
                                    multiple
                                    value={mistakeForm.leak_ids}
                                    onChange={(e) => setMistakeForm({...mistakeForm, leak_ids: Array.from(e.target.selectedOptions, option => option.value)})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    size="4"
                                >
                                    {leaks.map(leak => (
                                        <option key={leak.id} value={leak.id}>
                                            {leak.name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Hold Ctrl/Cmd to select multiple leaks
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={mistakeForm.description}
                                    onChange={(e) => setMistakeForm({...mistakeForm, description: e.target.value})}
                                    required
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    rows="3"
                                    placeholder="Describe what happened..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Cost Estimate <span className="text-gray-400">(optional)</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={mistakeForm.cost_estimate}
                                    onChange={(e) => setMistakeForm({...mistakeForm, cost_estimate: e.target.value})}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="50.00"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Estimated $ impact of the mistake
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowMistakeModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                >
                                    Add Mistake
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Chat Panel for post-session review */}
            <ChatPanel
                sessionId={parseInt(sessionId)}
                isOpen={chatPanelOpen}
                onClose={() => setChatPanelOpen(false)}
            />
        </div>
    );
}

export default SessionDetailsPage; 